import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { guessTimezoneOfBrowser } from '../../../../utils/functions';
import SwitcherButton from '../../../Buttons/CommonSwitcher';
import DateRangerPickerWithCustomTimezone from '../../../DateRangerPickerWithCustomTimezone/DateRangerPickerWithCustomTimezone';
import { RequiredTag } from '../../../Input/RequiredTag';
import CustomTableDragSelect from './CustomTableDragSelect/CustomTableDragSelect';
import doohEvenly from './icons/dooh_evenly.svg';
import doohAsap from './icons/dooh_asap.svg';
import doohOptimized from './icons/dooh_optimized.svg';
import doohInferredPath from './icons/dooh_inferred_path.svg';

const Timing = ({
    service,
    timing,
    totalSpent,
    dispatch,
    validator,
    onTimingChange,
}) => {
    const { t, i18n } = useTranslation();
    const [dateRangeError, setDateRangeError] = useState('');
    const dateRangeErrorTimerRef = useRef(null);
    const validatePeriodValueRef = useRef(validator.validateField);
    const loadedStartRef = useRef(null);

    const showDateRangeError = useCallback((message) => {
        setDateRangeError(message);
        if (dateRangeErrorTimerRef.current) {
            clearTimeout(dateRangeErrorTimerRef.current);
        }
        dateRangeErrorTimerRef.current = setTimeout(() => {
            setDateRangeError('');
            dateRangeErrorTimerRef.current = null;
        }, 5000);
    }, []);

    useEffect(() => {
        return () => {
            if (dateRangeErrorTimerRef.current) {
                clearTimeout(dateRangeErrorTimerRef.current);
            }
        };
    }, []);
    // spending per api-real campaign, to be able to test: eg: totalSpent = 10
    const isDayParting = timing.isDayParting;
    const rawStrategy = timing.strategy;
    const strategy =
        typeof rawStrategy === 'string' && rawStrategy.trim() !== ''
            ? rawStrategy.trim().toLowerCase()
            : 'evenly';

    const selectedTimezone = timing.selectedTimezone;

    // Display browser-locked timestamps back to Date (NO conversion).
    const periodValue = useMemo(() => {
        if (!timing.periodValue) return null;
        return [
            new Date(timing.periodValue[0]),
            new Date(timing.periodValue[1]),
        ];
    }, [timing.periodValue?.[0], timing.periodValue?.[1]]);
    const timezones = timing.timezones;

    useEffect(() => {
        validatePeriodValueRef.current = validator.validateField;
    }, [validator.validateField]);

    const isNegativeUtc = (timezone) => {
        return timezone <= 0;
    };
    const timezonesCopy = [];

    timezones.forEach((timezone) => {
        const newTimezone = Object.assign({}, timezone);
        newTimezone.label = `${timezone.description} | (UTC ${
            isNegativeUtc(timezone.gmt) ? timezone.gmt : '+' + timezone.gmt
        })`;
        timezonesCopy.push(newTimezone);
    });

    const FULL_DAY_SCHEDULE = '1'.repeat(24 * 7);

    const toggle = (attribute) => {
        let isToggledOn = !timing[attribute];
        change(attribute, isToggledOn);
    };

    const change = (attribute, value) => {
        const newTiming = { ...timing, [attribute]: value };

        if (attribute === 'isDayParting' && !value) {
            newTiming.hoursEnabled = FULL_DAY_SCHEDULE;
        } else if (attribute === 'isDayParting' && value) {
            newTiming.hoursEnabled = '0'.repeat(24 * 7);
        }

        onTimingChange(newTiming);
    };

    const modeOptions = useMemo(
        () => [
            {
                value: 'evenly',
                label: t(
                    'sections.campaigns.new-campaign.wizard-basics.timing.evenly'
                ),
                icon: doohEvenly,
                description: t(
                    'sections.campaigns.new-campaign.wizard-basics.timing.evenly-description'
                ),
            },
            {
                value: 'asap',
                label: t(
                    'sections.campaigns.new-campaign.wizard-basics.timing.ASAP'
                ),
                icon: doohAsap,
                description: t(
                    'sections.campaigns.new-campaign.wizard-basics.timing.asap-description'
                ),
            },
            {
                value: 'optimizer',
                label: t(
                    'sections.campaigns.new-campaign.wizard-basics.timing.optimizer'
                ),
                icon: doohOptimized,
                description: t(
                    'sections.campaigns.new-campaign.wizard-basics.timing.optimizer-description'
                ),
            },
            {
                value: 'viooh_distribution',
                label: t(
                    'sections.campaigns.new-campaign.wizard-basics.timing.inferred_path'
                ),
                icon: doohInferredPath,
                description: t(
                    'sections.campaigns.new-campaign.wizard-basics.timing.inferred_path-description'
                ),
            },
        ],
        [t, i18n.language]
    );

    const selectedMode =
        modeOptions.find((option) => option.value === strategy) ||
        modeOptions[0];

    const handleStrategyChange = (selected) => {
        change('strategy', selected.value);
    };

    const formatOptionLabel = (option) => (
        <div className="flex items-center gap-2">
            <img
                src={option.icon}
                alt=""
                className="h-6 w-auto rounded-sm"
            />
            <span>{option.label}</span>
        </div>
    );

    // store browser timezone, no shift, no UTC math.
    const convertStartDateToTimestamp = (date) => {
        if (!date) return null;
        return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            0,
            0,
            0,
            0
        ).getTime();
    };

    const convertEndDateToTimestamp = (date) => {
        if (!date) return null;
        return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            23,
            59,
            59,
            999
        ).getTime();
    };

    const hasCampaignSpent = () => {
        if (typeof totalSpent === 'number') {
            return totalSpent > 0;
        }

        const numericTotalSpent = Number(String(totalSpent).replace(/,/g, ''));
        return Number.isFinite(numericTotalSpent) && numericTotalSpent > 0;
    };

    const hasSpent = hasCampaignSpent();

    // Spent campaigns keep their stored start, including past dates. Every
    // other campaign can move its start, but never before today.
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    // Start as it was loaded, captured once so edits to the range can't move
    // the lock. New campaigns have no loaded start until the user picks one.
    if (loadedStartRef.current === null && timing.periodValue?.[0] != null) {
        loadedStartRef.current = timing.periodValue[0];
    }
    const loadedStart = loadedStartRef.current;
    const isStartLocked = hasSpent;

    const minDate = useMemo(() => {
        if (isStartLocked && loadedStart) {
            const locked = new Date(loadedStart);
            locked.setHours(0, 0, 0, 0);
            return locked;
        }
        return today;
    }, [isStartLocked, loadedStart, today]);

    useEffect(() => {
        if (hasSpent || !timing.periodValue) return;

        const startStamp = convertStartDateToTimestamp(
            new Date(timing.periodValue[0])
        );
        const todayStamp = today.getTime();
        if (startStamp >= todayStamp) return;

        const endStamp = convertEndDateToTimestamp(
            new Date(timing.periodValue[1])
        );
        change('periodValue', [
            todayStamp,
            Math.max(endStamp, convertEndDateToTimestamp(today)),
        ]);
    }, [hasSpent, timing.periodValue?.[0], timing.periodValue?.[1], today]);

    const handleChangePeriodValue = (period) => {
        if (!period) {
            change('periodValue', null);
            return;
        }

        const startStamp = convertStartDateToTimestamp(period[0]);
        const endStamp = convertEndDateToTimestamp(period[1]);

        const todayStart = new Date().setHours(0, 0, 0, 0);
        const storedStartStamp =
            loadedStart !== null && loadedStart !== undefined
                ? convertStartDateToTimestamp(new Date(loadedStart))
                : null;

        let isValid = false;
        if (!isStartLocked) {
            isValid =
                startStamp !== null &&
                endStamp !== null &&
                startStamp >= todayStart &&
                endStamp >= todayStart &&
                endStamp >= startStamp;
        } else {
            isValid =
                startStamp !== null &&
                endStamp !== null &&
                startStamp === storedStartStamp &&
                endStamp >= todayStart &&
                endStamp >= startStamp;
        }

        if (isValid) {
            change('periodValue', [startStamp, endStamp]);
            return;
        }

        showDateRangeError(
            t('sections.campaigns.new-campaign.wizard-basics.timing.end-date-before-today')
        );
    };

    const handleInvalidPeriodValue = useCallback(() => {
        validator.touchField('periodValue');
        validator.validateField('periodValue');
    }, [validator]);

    useEffect(() => {
        if (timezones.length === 0) {
            service.getTimezones().then((tzs) => change('timezones', tzs));
        }
    }, []);

    useEffect(() => {
        validator.validateField('dayParting');
    }, [timing.isDayParting, timing.hoursEnabled]);

    useEffect(() => {
        if (timing.periodValue) {
            validatePeriodValueRef.current('periodValue');
        }
    }, [
        timing.periodValue?.[0],
        timing.periodValue?.[1],
        totalSpent,
        isStartLocked,
    ]);

    useEffect(() => {
        if (timezones.length > 0 && selectedTimezone === '') {
            const timezone = guessTimezoneOfBrowser(timezones);
            change('selectedTimezone', timezone);
        }
    }, [timezones]);

    return (
        <div className="module rounded m-1 mb-4">
            <div className="header">
                <div className="title text-xl font-extralight">
                    {t(
                        'sections.campaigns.new-campaign.wizard-basics.timing.title'
                    )}
                </div>
            </div>
            <div className="body flex flex-col lg:flex-row gap-4">
                <div className="lg:w-3/6 2xl:w-2/6">
                    <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {t(
                                'sections.campaigns.new-campaign.wizard-basics.timing.description'
                            )}{' '}
                            <RequiredTag
                                alert={validator.errors?.periodValue}
                            />
                        </label>
                    </div>
                    <div
                        className="bg-transparent w-full text-gray-500 sm:text-sm rounded-md mt-2 mb-4"
                        onKeyDown={(event) => event.preventDefault()}>
                        <DateRangerPickerWithCustomTimezone
                            onChange={handleChangePeriodValue}
                            onInvalidRange={handleInvalidPeriodValue}
                            value={periodValue}
                            timezone={selectedTimezone}
                            totalSpent={totalSpent}
                            minDate={minDate}
                            lockStartDate={isStartLocked}
                            clearIcon={null}
                            className={`w-full sm:text-sm rounded-md outline-none transition-colors text-center ${
                                validator.errors.periodValue
                                    ? 'border-red-500 ring-1 ring-red-500 hover:border-red-500'
                                    : 'border-gray-300 focus:outline-none focus:ring-0 focus:outline-none hover:border-gray-400'
                            }`}
                            onBlur={() => {
                                validator.touchField('periodValue');
                                validator.validateField('periodValue');
                            }}
                        />
                        {isStartLocked && (
                            <span className="font-light block mt-4 text-sm text-gray-400">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-basics.timing.start-date-locked'
                                )}
                            </span>
                        )}
                        {dateRangeError && (
                            <p className="mt-2 text-sm text-red-500">
                                {dateRangeError}
                            </p>
                        )}
                        {validator.shouldShowError('periodValue') && (
                            <p className="mt-1 text-sm text-red-500">
                                {validator.errors.periodValue}
                            </p>
                        )}
                    </div>
                </div>
                {/* Pacing */}
                <div className="lg:w-3/6 2xl:w-2/6 pt-2">
                    <label className="block text-sm font-medium text-gray-700">
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.pacing'
                        )}
                    </label>
                    <div className="mt-2">
                        <Select
                            value={selectedMode}
                            options={modeOptions}
                            onChange={handleStrategyChange}
                            formatOptionLabel={formatOptionLabel}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isSearchable={false}
                            inputId="pacing" // data-testid
                        />
                    </div>
                    {selectedMode && (
                        <p className="text-sm text-gray-600 font-light mt-3 mb-6">
                            {selectedMode.description}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex justify-between mt-4">
                <label
                    htmlFor="day-parting"
                    className="block text-sm font-medium text-gray-700">
                    {t(
                        'sections.campaigns.new-campaign.wizard-basics.timing.day-parting'
                    )}{' '}
                    <span className="font-light">
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.day-parting-description'
                        )}
                    </span>
                    <RequiredTag alert={validator.errors?.dayParting} />
                </label>
                <div className="brief">
                    <SwitcherButton
                        onToggle={() => toggle('isDayParting')}
                        status={isDayParting}
                        labelOn={t('common.buttons.label-active')}
                        labelOff={t('common.buttons.label-inactive')}
                    />
                </div>
            </div>
            {isDayParting && (
                <>
                    <CustomTableDragSelect change={change} timing={timing} />
                    {validator.shouldShowError('dayParting') && (
                        <p className="mt-1 text-sm text-red-500">
                            {validator.errors.dayParting}
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

export default Timing;
